import React, { useState, useEffect, useContext, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Radio from '@atlaskit/radio/radio';
import Button from '@atlaskit/button/default/button';
import Link from '@atlaskit/link/link';
import Form from '@atlaskit/form/form';
import Field from '@atlaskit/form/field';
import { FormFooter } from '@atlaskit/form/form-footer';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner/spinner';
import { FlagsProvider } from '@atlaskit/flag/flag-provider';

import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { VerifiedTeamLozenge } from '@confluence/permissions-principals/entry-points/VerifiedTeamLozenge.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import { RestrictedPage } from '@confluence/no-permission/entry-points/RestrictedPage.ts';

import { FrictionModal } from './dialogs/FrictionModal.tsx';
import {
	useConfluenceSiteTeamConfigurations,
	ConfluenceTeamAdminControlsMode,
} from './useConfluenceSiteTeamConfigurations.tsx';

const i18n = defineMessages({
	title: {
		id: 'team-admin-controls.admin-controls.page.title',
		defaultMessage: 'Teams',
		description:
			'The text is shown as the main page heading at the top of the admin controls page, which allows administrators to manage team-related settings for their organisation.',
	},
	manageTeamsButton: {
		id: 'team-admin-controls.admin-controls.page.manage-teams-button',
		defaultMessage: 'Manage teams',
		description: 'Button that navigates to the team management administration page',
	},
	description1: {
		id: 'team-admin-controls.admin-controls.page.description1',
		defaultMessage:
			'Decide whether users on your site can assign access to teams (for example, by adding a team to a space and assigning it specific access). When enabled, users can assign access to teams, in addition to groups and individual users.',
		description: 'Description paragraph 1 for the admin controls page',
	},
	description2: {
		id: 'team-admin-controls.admin-controls.page.description2',
		defaultMessage:
			'Atlassian teams let you collaborate quickly and easily with your project team across most Atlassian apps by sharing important links and resources, easily notifying all members, tracking work across apps on the team profile, and more. <link>Learn more about teams</link>',
		description: 'Description paragraph 2 for the admin controls page',
	},
	radioGroupLabel: {
		id: 'team-admin-controls.admin-controls.page.radio-group-label',
		defaultMessage: 'Which teams can be assigned access?',
		description: 'Label for the radio group to select which team types can be assigned access',
	},
	radioGroupOption1: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-1',
		defaultMessage: 'All teams',
		description: 'Radio button option to allow any team type to be assigned access',
	},
	radioGroupOption2: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-2',
		defaultMessage: 'Only admin-managed teams',
		description: 'Radio button option to only allow verified teams to be assigned access',
	},
	radioGroupOption3: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-3',
		defaultMessage: 'No teams can be assigned access',
		description: 'Radio button option to disable all teams from being assigned access',
	},
	radioGroupDescription1: {
		id: 'team-admin-controls.admin-controls.page.radio-group-description-1',
		defaultMessage: 'Both admin-managed and member-managed teams',
		description:
			'Description for the radio group option to allow any team type to be assigned access',
	},
	radioGroupDescription2: {
		id: 'team-admin-controls.admin-controls.page.radio-group-description-2',
		defaultMessage:
			'All admin-managed teams will be marked with a blue check. <link>More about admin-managed teams</link>',
		description:
			'Description for the radio group option to only allow verified teams to be assigned access',
	},
	radioGroupDescription3: {
		id: 'team-admin-controls.admin-controls.page.radio-group-description-3',
		defaultMessage: 'Access can only be assigned to groups and individual users',
		description:
			'Description for the radio group option to disable all teams from being assigned access',
	},
	membershipControlRadioGroupLabel: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-label',
		defaultMessage: 'Membership control',
		description: 'Label for the membership control radio group',
	},
	membershipControlRadioGroupOption1: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-option-1',
		defaultMessage: 'Allow teams that anyone can join',
		description: 'Option 1 for the membership control radio group',
	},
	membershipControlRadioGroupOption2: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-option-2',
		defaultMessage: 'Only allow teams that require approval to join',
		description: 'Option 2 for the membership control radio group',
	},
	saveButton: {
		id: 'team-admin-controls.admin-controls.page.save-button',
		defaultMessage: 'Save',
		description: 'Button to save the selected team access settings to apply them',
	},
	cancelButton: {
		id: 'team-admin-controls.admin-controls.page.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Button to cancel any unsaved changes to team access settings',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.800'),
		paddingInline: token('space.800'),
		maxWidth: '1200px',
		marginInline: 'auto',
	},
	description: {
		marginInlineEnd: token('space.1000'),
	},
	radioGroupDescription: {
		marginInlineStart: token('space.400'),
	},
});

const membershipControlRadioOptions = [
	{
		value: 'open-membership',
		label: <FormattedMessage {...i18n.membershipControlRadioGroupOption1} />,
	},
	{
		value: ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS,
		label: <FormattedMessage {...i18n.membershipControlRadioGroupOption2} />,
	},
];

export const LEARN_MORE_LINK =
	'https://support.atlassian.com/platform-experiences/docs/teams-in-confluence-permissions/';

const AdminControlsContent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, orgId, edition } = useSessionData();
	const { isProductAdmin } = useIsProductAdmin();

	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	const onClickManageTeams = () => {
		window.open(href, '_blank');
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'clicked manage teams button',
			},
		}).fire();
	};

	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		siteConfigurationLoading,
		currentTeamAdminControlsMode,
		updateTeamAdminControlsSettings,
		updateTeamAdminControlsSettingsLoading,
	} = useConfluenceSiteTeamConfigurations();
	const [hasChanged, setHasChanged] = useState(false);
	const [membershipControlType, setMembershipControlType] = useState<string>('open-membership');
	const [lastSavedMembershipControlType, setLastSavedMembershipControlType] =
		useState<string>('open-membership');
	const [showFrictionModal, setShowFrictionModal] = useState(false);
	const [selectedTeamAccessType, setSelectedTeamAccessType] = useState<
		ConfluenceTeamAdminControlsMode | undefined
	>(undefined);
	// Track the last saved mode locally to avoid stale server data affecting shouldShowFrictionModal
	const [lastSavedMode, setLastSavedMode] = useState<ConfluenceTeamAdminControlsMode | undefined>(
		undefined,
	);

	useEffect(() => {
		if (!siteConfigurationLoading && currentTeamAdminControlsMode) {
			if (currentTeamAdminControlsMode === ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS) {
				setSelectedTeamAccessType(ConfluenceTeamAdminControlsMode.ALL_TEAMS);
				setLastSavedMode(ConfluenceTeamAdminControlsMode.ALL_TEAMS);

				const membershipControlTypeValue = expVal(
					'confluence_frontend_perms_teams_ga_fast_follow',
					'isEnabled',
					false,
				)
					? ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS
					: 'open-membership';

				setMembershipControlType(membershipControlTypeValue);
				setLastSavedMembershipControlType(membershipControlTypeValue);
			} else {
				setSelectedTeamAccessType(currentTeamAdminControlsMode as ConfluenceTeamAdminControlsMode);
				setLastSavedMode(currentTeamAdminControlsMode as ConfluenceTeamAdminControlsMode);
			}
		}
	}, [siteConfigurationLoading, currentTeamAdminControlsMode]);

	const onTeamAccessTypeChange = (value: string) => {
		setHasChanged(value !== lastSavedMode);
		setSelectedTeamAccessType(value as ConfluenceTeamAdminControlsMode);

		if (value !== ConfluenceTeamAdminControlsMode.ALL_TEAMS) {
			setMembershipControlType(lastSavedMembershipControlType);
		}
	};

	const onMembershipControlTypeChange = (value: string) => {
		setHasChanged(value !== lastSavedMembershipControlType);
		setMembershipControlType(value);
	};

	// We want to show the friction modal only when there is possible access loss:
	// so going from All teams -> Verified/No teams, or Verified -> No teams
	// Going from No teams -> any level above will result in access gain so we don't show the modal.
	// We use lastSavedMode (local state) instead of currentTeamAdminControlsMode (server state)
	// to avoid stale data issues after a save.
	const shouldShowFrictionModal = useCallback(() => {
		if (lastSavedMode === ConfluenceTeamAdminControlsMode.ALL_TEAMS) {
			return true;
		} else if (
			lastSavedMode === ConfluenceTeamAdminControlsMode.VERIFIED_TEAMS &&
			selectedTeamAccessType === ConfluenceTeamAdminControlsMode.NO_TEAMS
		) {
			return true;
		} else if (lastSavedMode === ConfluenceTeamAdminControlsMode.NO_TEAMS) {
			return false;
		}
		return false;
	}, [lastSavedMode, selectedTeamAccessType]);

	const onSaveClick = () => {
		if (shouldShowFrictionModal()) {
			setShowFrictionModal(true);
		} else {
			void onConfirmFrictionModal();
		}
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'saved team access type',
				attributes: {
					teamAccessType: selectedTeamAccessType || '(empty)',
					membershipControlType,
				},
			},
		}).fire();
	};

	const onConfirmFrictionModal = async () => {
		setShowFrictionModal(false);
		experienceTracker.start({
			name: 'updateTeamAdminControlsSettings',
		});
		if (
			selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS &&
			membershipControlType === ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS
		) {
			await updateTeamAdminControlsSettings(ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS);
			setLastSavedMembershipControlType(membershipControlType);
			setMembershipControlType(membershipControlType);
		} else {
			await updateTeamAdminControlsSettings(
				selectedTeamAccessType as ConfluenceTeamAdminControlsMode,
			);
			setLastSavedMembershipControlType('open-membership');
			setMembershipControlType('open-membership');
		}
		setLastSavedMode(selectedTeamAccessType);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'confirmed friction modal',
				attributes: {
					teamAccessType: selectedTeamAccessType,
					membershipControlType,
				},
			},
		}).fire();
		setHasChanged(false);
	};
	if (!isProductAdmin || edition === ConfluenceEdition.FREE) {
		return <RestrictedPage />;
	}

	const onCancel = () => {
		setHasChanged(false);
		setShowFrictionModal(false);
		setSelectedTeamAccessType(lastSavedMode);
		setMembershipControlType(lastSavedMembershipControlType);
	};

	return siteConfigurationLoading ? (
		<Spinner />
	) : (
		<Box
			xcss={
				expVal('cc_perms_enable_rbac_admin_modernization', 'isEnabled', false)
					? undefined
					: styles.container
			}
		>
			{showFrictionModal && selectedTeamAccessType && (
				<FrictionModal
					width="medium"
					teamAccessType={selectedTeamAccessType}
					onConfirm={() => onConfirmFrictionModal()}
					onClose={() => setShowFrictionModal(false)}
				/>
			)}
			<Stack space="space.300">
				{/* Header with title and Manage teams button */}
				<Flex justifyContent="space-between" alignItems="center">
					<Heading size="large">{formatMessage(i18n.title)}</Heading>
					<Button
						iconBefore={LinkExternalIcon}
						appearance={
							expVal('cc_perms_enable_rbac_admin_modernization', 'isEnabled', false)
								? 'default'
								: 'subtle'
						}
						onClick={() => onClickManageTeams()}
					>
						{formatMessage(i18n.manageTeamsButton)}
					</Button>
				</Flex>

				{/* Description with Learn more link */}
				<Stack space="space.200" xcss={styles.description}>
					<Text>{formatMessage(i18n.description1)}</Text>
					<Text>
						{formatMessage(i18n.description2, {
							link: (chunks: React.ReactNode[]) => (
								<Link href={LEARN_MORE_LINK} target="_blank">
									{chunks}
								</Link>
							),
						})}
					</Text>
				</Stack>

				{/* Form with radio group */}
				<Box paddingBlockStart="space.300">
					<Form
						onSubmit={() => {
							// TODO: Implement save functionality
							return Promise.resolve();
						}}
					>
						{({ formProps }) => (
							<form {...formProps}>
								<Stack space="space.200" spread="space-between">
									<Heading size="small">{formatMessage(i18n.radioGroupLabel)}</Heading>
									{siteConfigurationLoading ? (
										<Spinner />
									) : (
										<Field
											name="teamAccessType"
											defaultValue={currentTeamAdminControlsMode ?? undefined}
										>
											{({ fieldProps }) => (
												<Stack space="space.100" spread="space-between">
													<Radio
														{...fieldProps}
														value={ConfluenceTeamAdminControlsMode.ALL_TEAMS}
														label={formatMessage(i18n.radioGroupOption1)}
														onChange={(e) =>
															onTeamAccessTypeChange(
																e.target.value as ConfluenceTeamAdminControlsMode,
															)
														}
														isChecked={
															selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS
														}
													/>
													<Inline
														alignInline="start"
														space="space.050"
														xcss={styles.radioGroupDescription}
													>
														<Text size="small" color="color.text.subtlest">
															{formatMessage(i18n.radioGroupDescription1)}
														</Text>
													</Inline>

													<Radio
														{...fieldProps}
														value={ConfluenceTeamAdminControlsMode.VERIFIED_TEAMS}
														label={formatMessage(i18n.radioGroupOption2)}
														onChange={(e) =>
															onTeamAccessTypeChange(
																e.target.value as ConfluenceTeamAdminControlsMode,
															)
														}
														isChecked={
															selectedTeamAccessType ===
															ConfluenceTeamAdminControlsMode.VERIFIED_TEAMS
														}
													/>
													<Inline
														alignInline="start"
														alignBlock="center"
														space="space.050"
														xcss={styles.radioGroupDescription}
													>
														<VerifiedTeamLozenge spacing="none" />
														<Text size="small" color="color.text.subtlest">
															{formatMessage(i18n.radioGroupDescription2, {
																link: (chunks: React.ReactNode[]) => (
																	<Link href={LEARN_MORE_LINK} target="_blank">
																		{chunks}
																	</Link>
																),
															})}
														</Text>
													</Inline>

													<Radio
														{...fieldProps}
														value={ConfluenceTeamAdminControlsMode.NO_TEAMS}
														label={formatMessage(i18n.radioGroupOption3)}
														onChange={(e) =>
															onTeamAccessTypeChange(
																e.target.value as ConfluenceTeamAdminControlsMode,
															)
														}
														isChecked={
															selectedTeamAccessType === ConfluenceTeamAdminControlsMode.NO_TEAMS
														}
													/>
													<Inline
														alignInline="start"
														space="space.050"
														xcss={styles.radioGroupDescription}
													>
														<Text size="small" color="color.text.subtlest">
															{formatMessage(i18n.radioGroupDescription3)}
														</Text>
													</Inline>
												</Stack>
											)}
										</Field>
									)}
									{selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS &&
										expVal(
											'confluence_frontend_perms_teams_ga_fast_follow',
											'isEnabled',
											false,
										) && (
											<>
												<Heading size="small">
													{formatMessage(i18n.membershipControlRadioGroupLabel)}
												</Heading>
												<Field name="membershipControlType" defaultValue={membershipControlType}>
													{({ fieldProps: membershipFieldProps }) => (
														<Stack space="space.150" spread="space-between">
															<Radio
																{...membershipFieldProps}
																value={membershipControlRadioOptions[0].value}
																label={membershipControlRadioOptions[0].label}
																onChange={(e) => onMembershipControlTypeChange(e.target.value)}
																isChecked={
																	membershipControlType === membershipControlRadioOptions[0].value
																}
															/>
															<Radio
																{...membershipFieldProps}
																value={membershipControlRadioOptions[1].value}
																label={membershipControlRadioOptions[1].label}
																onChange={(e) => onMembershipControlTypeChange(e.target.value)}
																isChecked={
																	membershipControlType === membershipControlRadioOptions[1].value
																}
															/>
														</Stack>
													)}
												</Field>
											</>
										)}
								</Stack>
								<FormFooter align="start">
									<Inline space="space.050">
										<Button
											type="submit"
											isDisabled={!hasChanged}
											isLoading={updateTeamAdminControlsSettingsLoading}
											appearance="primary"
											onClick={() => onSaveClick()}
										>
											{formatMessage(i18n.saveButton)}
										</Button>
										<Button
											type="button"
											appearance="subtle"
											onClick={onCancel}
											isDisabled={!hasChanged}
										>
											{formatMessage(i18n.cancelButton)}
										</Button>
									</Inline>
								</FormFooter>
							</form>
						)}
					</Form>
				</Box>
			</Stack>
		</Box>
	);
};

export const AdminControls = () => (
	<FlagsProvider>
		<AdminControlsContent />
	</FlagsProvider>
);
